import { FC, useCallback, useEffect } from "react";
import Input from "apps/website/components/form/Input/Input";
import {
  FieldData,
  getFieldValue,
} from "libs/state/src/lib/stores/useFormServiceStore";
import { FieldMap, InputField } from "libs/forms-schema/src/types";
import {
  calculateCatWeight,
} from "libs/cat-calculations/src/lib/cat-calculations";
import {
  AgeType,
  Gender,
  ICatAge,
} from "libs/cat-calculations/src/lib/cat-calculations.types";
import { Breed } from "@forms/schema";
import cx from "apps/website/utils/misc/cx";

import Icon from "../../base/Icon/Icon";
import TextBody from "../../base/Text/TextBody/TextBody";

export interface IWeightInput {
  flowId: string;
  field: InputField & { fields: FieldMap[] };
  value?: FieldData;
  onChange(value: FieldData): void;
  linkingId: string;
  isValid?: boolean;

}

const Arrow = () => <div className="absolute -top-2 left-6 w-0 h-0 border-x-8 border-x-transparent border-b-8 border-b-[var(--color-light-grey)] "></div>;

const Tooltip = ({ pronoun, catWeight, catName, catActivity }:
{ pronoun: string, catWeight: string, catName: string, catActivity: string }) => (
  <div role="tooltip" className={cx("bg-[var(--color-light-grey)] h-11 mt-2 p-2 relative", (catActivity?.length || 0) < 1 && "hidden")}>

    <Arrow/>

    <div className="flex lg:justify-center lg:items-center">
      <div className="pr-2">
        <Icon icon={"informationHeavy"} size="xsmall" />
      </div>

      <TextBody className="text-2xs md:text-3xs lg:text-2xs">
        { `${catName} probably weighs around ` }<strong>{ catWeight }kg</strong>{ `, based on ${pronoun} age, gender and breed.` }
      </TextBody>
    </div>
  </div>
);

export const WeightInput: FC<IWeightInput> = ({
  flowId,
  field,
  value,
  onChange,
  isValid,
  linkingId,
}) => {

  const catActivity = getFieldValue(flowId, "catActivity", linkingId)?.data.submitValue as string;

  const catBreed = getFieldValue(flowId, "catBreed", linkingId)?.data.submitValue as Breed;
  const catSex = getFieldValue(flowId, "catSex", linkingId)?.data.submitValue as Gender;
  const catAgeYears = getFieldValue(flowId, "catAgeYears", linkingId)?.data.submitValue as number;
  const catAgeMonths = getFieldValue(flowId, "catAgeMonths", linkingId)?.data.submitValue as number;
  const catAgeWeeks = getFieldValue(flowId, "catAgeWeeks", linkingId)?.data.submitValue as number;
  const catLifeStage = getFieldValue(flowId, "catLifeStage", linkingId)?.data.submitValue as AgeType;
  const catName = getFieldValue(flowId, "catName", linkingId)?.data.submitValue as string;

  const catAge = {
    type: catLifeStage || "Adult",
    years: catAgeYears || 0,
    months: catAgeMonths || 0,
    weeks: catAgeWeeks || 0,
  } as ICatAge;

  const weight = calculateCatWeight(catBreed, catAge, catSex).toFixed(1);

  useEffect(() => {

    if (getFieldValue(flowId, "catWeight", linkingId)?.data.submitValue) {
      return;
    }

    onChange({
      submitValue: weight,
      displayValue: weight,
    });
  }, [ weight ]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = event.target.value;

    // Only allow numbers and one decimal point
    if (!/^\d*(?:\.\d*)?$/.test(numValue)) {
      return;
    }

    onChange({
      submitValue: numValue,
      displayValue: numValue,
    });
  }, [ onChange ]);

  return (
    <>
      <div
        className={cx(
          "relative flex items-center",
          (catActivity?.length || 0) < 1 && "hidden",
        )}
        data-testid="weight-input-container"
      >

        <div className="w-16">
          <Input
            type="text"
            name={field.name}
            label={`${catSex === "BOY" ? "He" : "She"} weighs about`}
            labelStyle="alternative"
            placeholder={weight}
            onChange={handleChange}
            value={value?.displayValue as string}
            isValid={isValid}
            inputClassName="rounded-lg w-16 pr-1"
            shouldLabelWrap={false}
            data-testid="weight-input"
          />
        </div>
        <div className="font-display pl-1">
          KG
        </div>
      </div>
      <Tooltip
        catName={catName}
        catWeight={weight}
        pronoun={catSex === "BOY" ? "his" : "her"}
        catActivity={catActivity}
      />
    </>
  );
};
